import {
  Component,
  computed,
  ElementRef,
  inject,
  OnDestroy,
  Signal,
  signal,
  viewChild,
  WritableSignal
} from '@angular/core';
import { Admin as AdminService } from './admin.svc';
import { LightboxService, StorageService } from '@core/services';
import { SvgIconComponent } from 'angular-svg-icon';
import posthog from 'posthog-js';
import { DatePipe, DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AcknowledgmentComponent as AdminAcknowledgmentComponent,
  AddContactComponent as AdminAddContactComponent
} from './components';
import { first, take } from 'rxjs';
import {
  AdminAcknowledgmentResponse,
  ContactFilterOption,
  ContactPayload,
  PortalRolesGroup,
  SanitizedContact,
  SanitizedPortalRole
} from './SanitizedData';

interface Messenger {
  message: string;
  type: 'alert' | 'notice';
}
type NewContact = Pick<SanitizedContact, 'businessSegments' | 'email' | 'esaaStatus' | 'id' | 'name' | 'segments'> & {
  docusignId?: string;
  isExistingContact: boolean;
};
@Component({
  selector: 'oke-admin',
  templateUrl: './admin.html',
  styleUrl: './admin.scss',
  imports: [SvgIconComponent, DatePipe]
})
export class Admin implements OnDestroy {
  /**
   * Class Properties
   */
  private readonly _lightboxSvc: LightboxService = inject(LightboxService);
  private readonly _adminSvc: AdminService = inject(AdminService);
  private readonly _storageSvc: StorageService = inject(StorageService);
  private readonly _$allAccountContacts = signal<SanitizedContact[]>([]);
  protected readonly $longestContactNameNumChars = computed(() =>
    this._adminSvc.onComputed.longestContactNameNumChars(this.$visibleAccountContacts())
  );
  protected readonly $contactsFilterOptions = signal<ContactFilterOption[]>([
    { label: 'All Users', selected: false, value: 'all' },
    { label: 'Active Users', selected: true, value: 'status:active' },
    { label: 'Inactive Users', selected: false, value: 'status:inactive' }
  ]);
  protected readonly $contactsSearchValue: WritableSignal<string | null> = signal<string | null>(null);
  protected readonly $contactsSortValue: WritableSignal<'name--a' | 'name--d' | 'login-date--a' | 'login-date--d'> =
    signal('name--d');
  private readonly _$messenger: WritableSignal<Messenger | null> = signal<Messenger | null>(null);
  protected readonly $messenger: Signal<Messenger | null> = computed(() => this._$messenger());
  private readonly _$portalAccountRoles: WritableSignal<SanitizedPortalRole[]> = signal<SanitizedPortalRole[]>([]);
  private readonly _$isAccountAdminAcknowledgmentShown: WritableSignal<boolean> = signal<boolean>(false);
  protected readonly $allAccountContacts = computed(() =>
    this._adminSvc.onComputed.allAccountContacts(
      this._$allAccountContacts(),
      this.$contactsFilterOptions(),
      this.$contactsSearchValue() ?? '',
      this.$contactsSortValue()
    )
  );
  private readonly _$hasQueriedForContacts = signal(false);
  protected readonly $hasQueriedForContacts = this._$hasQueriedForContacts.asReadonly();
  protected readonly $isApiLoading: Signal<boolean> = computed(() => this._adminSvc.$isApiLoading());
  protected readonly $isCanAddNewContact: Signal<boolean> = computed(() =>
    this._adminSvc.onComputed.isCanAddNewContact(this._$isAccountAdminAcknowledgmentShown())
  );
  protected readonly $isCanSaveContacts: Signal<boolean> = computed(() =>
    this._adminSvc.onComputed.isCanSaveContacts(this.$updatedContacts())
  );
  protected readonly $portalAccountRoles: Signal<SanitizedPortalRole[]> = this._$portalAccountRoles.asReadonly();
  protected readonly $showAccountAdminAcknowledgment: Signal<boolean> =
    this._$isAccountAdminAcknowledgmentShown.asReadonly();
  protected readonly $updatedContacts: Signal<ContactPayload[]> = computed(() =>
    this._adminSvc.onComputed.updatedContacts(this._$allAccountContacts(), this._allAccountContactsCachedRoles)
  );
  protected readonly $visibleAccountContacts = computed(() =>
    this._adminSvc.onComputed.visibleAccountContacts(this.$allAccountContacts())
  );
  private _allAccountContactsCachedRoles: ContactPayload[] = [];
  protected readonly accountId: string = this._adminSvc.accountId;
  protected readonly accountName: string = this._adminSvc.accountName.replaceAll(' ', '&nbsp;');
  private readonly _doc: Document = inject(DOCUMENT);

  constructor() {
    const onDetermineAccountAdminAcknowledgedStatusSuccess = (response: AdminAcknowledgmentResponse): void => {
      if (response.status === 'Unagreed') {
        this._$isAccountAdminAcknowledgmentShown.set(true);
        this._lightboxSvc.openComponent(AdminAcknowledgmentComponent, {
          outputs: {
            adminAcknowledgmentSubmitted: this.submitAccountAdminAcknowledgment.bind(this)
          },
          isClosable: false
        });
      }
      this._getAdminApiData();
    };
    const onDetermineAccountAdminAcknowledgedStatusError = (error: HttpErrorResponse): void => {
      void error;
      this._$messenger.set({
        message: `<p>Could not process Admin Acknowledgment at this time for ${this._adminSvc.accountName}. Please try again, or contact Support.</p>`,
        type: 'alert'
      });
      this._$isAccountAdminAcknowledgmentShown.set(true);
      this._lightboxSvc.openComponent(AdminAcknowledgmentComponent, {
        outputs: {
          adminAcknowledgmentSubmitted: this.submitAccountAdminAcknowledgment.bind(this)
        },
        isClosable: false
      });
    };
    this._storageSvc.setCurrentPage('Admin');
    this._adminSvc
      .determineAccountAdminAcknowledgedStatus()
      .then(onDetermineAccountAdminAcknowledgedStatusSuccess)
      .catch(onDetermineAccountAdminAcknowledgedStatusError);
  }

  ngOnDestroy(): void {
    this._storageSvc.currentUser$.pipe(take(1)).subscribe(user => {
      if (!user?.Contact) {
        return;
      }
      user.Contact.IsAdminAgreementAcknowledged = false;
      this._storageSvc.setCurrentUser(user);
    });
  }

  /**
   * @description Template method "clears" the messenger Signal so whatever feedback was
   *   previously shown to the User is removed/hidden
   */
  protected clearMessenger(evt: Event): void {
    void evt;
    this._$messenger.set(null);
  }

  /**
   * @description Template "getter" that retrieves the currently selected
   *   Contacts-list Filter **value**.
   * @returns ReturnType<() => ContactsFilterValue['value']>
   */
  protected get contactsFilterValue(): string {
    return this.$contactsFilterOptions().find(({ selected: isSelected }) => isSelected === true)?.value ?? 'all';
  }

  /**
   * @description Component method fetches _contacts_ & _roles_ data
   *   from the API for manipulation in the UI. Secondarily, it also
   *   determines the Contacts-list "filter" values, and ESAA status.
   */
  private _getAdminApiData(): void {
    this._adminSvc
      .getAdminData()
      .then(({ contacts, portalRoles, contactsFilterOptions }) => {
        this._$allAccountContacts.set(contacts);
        this._$portalAccountRoles.set(portalRoles);
        this._allAccountContactsCachedRoles = this._adminSvc.deepFreeze(
          this._adminSvc.getContactsRolesPayload(contacts)
        );
        this.$contactsFilterOptions.update($contactsFilterOptions => {
          (contactsFilterOptions || []).forEach(contactsFilterOption =>
            $contactsFilterOptions.push(contactsFilterOption)
          );
          return structuredClone($contactsFilterOptions);
        });
      })
      .catch((error: HttpErrorResponse) => {
        void error;
        this._$allAccountContacts.set([]);
        this._$messenger.set({
          message: `<p>There was a problem retrieving Admin data for ${this._adminSvc.accountName}. Please try again, or contact Support.</p>`,
          type: 'alert'
        });
      })
      .finally(() => this._$hasQueriedForContacts.set(true));
  }

  /**
   * @description
   */
  protected dispatchContactInvitation(clickEvt: Event, contactId: string): void {
    const btnElem = clickEvt.target as HTMLButtonElement;
    const contact = this._$allAccountContacts().find(({ id }) => id === contactId);
    if (!contact) {
      return;
    }
    const {
      name: { first: NAME_FIRST, last: NAME_LAST, full: NAME_FULL }
    } = contact;
    const onDispatchContactInvitationSuccess = (): void => {
      this._$messenger.set({
        message: `<p>A Customer Portal invitation has been send to <strong>${NAME_FULL}</strong> for access to ${this._adminSvc.accountName}'s data.</p>`,
        type: 'notice'
      });
      this._$allAccountContacts.update($allAccountContacts => {
        const updatedContact = $allAccountContacts.find(({ id }) => id === contactId);
        if (updatedContact) {
          updatedContact.status.onboard = 'invite sent';
        }
        return structuredClone($allAccountContacts);
      });
    };
    const onDispatchContactInvitationError = (error: HttpErrorResponse): void => {
      void error;
      this._$messenger.set({
        message: `<p>A problem ocurred when trying to send <strong>${NAME_FULL}</strong> a Customer Portal invitation. Please try again, or contact Support.</p>`,
        type: 'alert'
      });
    };
    btnElem.classList.add('is-waiting');
    this._adminSvc
      .dispatchContactInvitation({
        nameFirst: NAME_FIRST,
        nameLast: NAME_LAST,
        recipientId: contactId
      })
      .then(onDispatchContactInvitationSuccess)
      .catch(onDispatchContactInvitationError)
      .finally(() => btnElem.classList.remove('is-waiting'));
  }

  /**
   * @description Template method handles the "saving" of Contact(s')
   *   permissions. Able to save _multiple_ contacts' or _only_ one
   * @param btnElem HTMLButtonElement
   * @param contactId (Optional) ReturnType<() => SanitizedContact['id']>
   */
  protected saveContactPermissions(btnElem: EventTarget, contactId?: string): void {
    /**
     * @description …
     * @param responses `{
     *   status: 'fulfilled'|'rejected';
     *   value: {
     *     UserMessage: string;
     *     request: {
     *       email: ReturnType<() => SanitizedContact['email']>;
     *       roleId: ReturnType<() => SanitizedPortalRole['id']>;
     *       selected: boolean;
     *     }
     *   }|{
     *     error: HttpErrorResponse;
     *     request: {
     *       email: ReturnType<() => SanitizedContact['email']>;
     *       roleId: ReturnType<() => SanitizedPortalRole['id']>;
     *       selected: boolean;
     *     }
     *   };
     * }[]`
     */
    const onSaveContactPermissionsSuccess = (responses: any[]): void => {
      (btnElem as HTMLElement).classList.remove('is-waiting');
      const isAllFulfilled = responses.every(({ status: responseStatus }) => responseStatus === 'fulfilled');
      const isAllRejected = responses.every(({ status: responseStatus }) => responseStatus === 'rejected');
      const handleFulfilledRequests = (responses: any[]): void => {
        //   API success
        this._allAccountContactsCachedRoles = this._adminSvc.deepFreeze(
          this._adminSvc.getContactsRolesPayload(this._$allAccountContacts())
        );
        // Make `false` the `isQueuedForUpdate` property
        this._$allAccountContacts.update($allAccountContacts => {
          return structuredClone(
            $allAccountContacts.map($accountContact => ({
              ...$accountContact,
              isQueuedForUpdate: false
            }))
          );
        });

        // Reset the select list to the first element after successful save
        const contactsListSelect = this._doc.getElementsByClassName(
          'contacts-list'
        ) as HTMLCollectionOf<HTMLSelectElement> | null;
        if (contactsListSelect !== null) {
          //set all contacts-list selects to first option
          for (const contactsListSelectElement of Array.from(contactsListSelect)) {
            contactsListSelectElement.selectedIndex = 0;
          }
        }
        this._$messenger.set({
          message: `<p><strong>${responses
            .map(v => v.request.email)
            .map(e => this._$allAccountContacts().find(({ email }) => email === e)?.name.full ?? '')
            .join(', ')
            .split(', ')
            .filter((name, idx, arr) => arr.indexOf(name) === idx)
            .join(', ')}</strong>'s updates have been saved!</p>`,
          type: 'notice'
        });
      };
      const handleRejectedRequests = (responses: any[]): void => {
        this._$messenger.set({
          message: `<p>Changes to User(s) <strong>${responses
            .map(v => v.request.email)
            .map(e => this._$allAccountContacts().find(({ email }) => email === e)?.name.full ?? '')
            .join(', ')
            .split(', ')
            .filter((name, idx, arr) => arr.indexOf(name) === idx)
            .join(', ')}</strong> could not be saved. Please try again, or reach out to Support.</p>`,
          type: 'alert'
        });
      };
      if (isAllFulfilled) {
        handleFulfilledRequests(
          responses
            .filter(({ status: responseStatus }) => responseStatus === 'fulfilled')
            .map(response => ({
              UserMessage: 'UserMessage' in response.value ? response.value.UserMessage : '',
              request: response.value.request
            }))
        );
      } else if (isAllRejected) {
        handleRejectedRequests(
          responses
            .filter(({ status: responseStatus }) => responseStatus === 'rejected')
            .map(response => ({
              error: 'error' in response.value ? response.value.error : new HttpErrorResponse({ error: '' }),
              request: response.value.request
            }))
        );
      } else if (responses.length) {
        const REJECTED_REQUESTS = responses
          .filter(({ status: responseStatus }) => responseStatus === 'rejected')
          .map(({ value: { request } }) => ({
            ...request
          }));
        const FULFILLED_REQUESTS = responses
          .filter(({ status: responseStatus }) => responseStatus === 'fulfilled')
          .map(({ value: { request } }) => ({
            ...request
          }));
        this._$allAccountContacts.update($allAccountContacts => {
          $allAccountContacts.map($accountContact => ({
            ...$accountContact,
            isQueuedForUpdate: false
          }));
          REJECTED_REQUESTS.forEach(
            ({
              email: rejectedRequestContactEmail,
              roleId: rejectedRequestRoleId,
              selected: rejectedRequestRoleIsSelected
            }) => {
              const rejectedContact = $allAccountContacts.find(
                ({ email: accountContactEmail }) => accountContactEmail === rejectedRequestContactEmail
              );
              const rejectedSegment = rejectedContact?.segments.find(({ list }) =>
                list.some(({ id }) => id === rejectedRequestRoleId)
              );
              const rejectedRole = rejectedSegment?.list.find(({ id }) => id === rejectedRequestRoleId);
              if (rejectedRole) {
                rejectedRole.selected = !rejectedRequestRoleIsSelected;
              }
            }
          );
          return structuredClone($allAccountContacts);
        });
        this._allAccountContactsCachedRoles = this._adminSvc.deepFreeze(
          this._adminSvc.getContactsRolesPayload(this._$allAccountContacts())
        );
        const SAVED_LIST_ELEM_STRING =
          '<ul>' +
          FULFILLED_REQUESTS.map(({ email, roleId, selected }) => {
            return `<li>${this._$allAccountContacts().find(({ email: e }) => e === email)?.name.full ?? email}: ${this._$portalAccountRoles().find(({ id }) => id === roleId)?.name ?? roleId} ${selected ? '✔' : '⨉'}</li>`;
          }).join('') +
          '</ul>';
        const UNSAVED_LIST_ELEM_STRING =
          '<ul>' +
          REJECTED_REQUESTS.map(({ email, roleId, selected }) => {
            return `<li>${this._$allAccountContacts().find(({ email: e }) => e === email)?.name.full ?? email}: ${this._$portalAccountRoles().find(({ id }) => id === roleId)?.name ?? roleId} ${selected ? '✔' : '⨉'}</li>`;
          }).join('') +
          '</ul>';
        this._$messenger.set({
          message: `<p>Some User(s) changes were saved &amp; others were not.</p><p><strong>Saved</strong>:</p>${SAVED_LIST_ELEM_STRING}<p><strong><em>Un</em>saved</strong>:</p>${UNSAVED_LIST_ELEM_STRING}<p>Please try the <em>unsaved</em> User(s) changes again, or contact Support.</p>`,
          type: 'alert'
        });
      }
    };
    /**
     * @description Utility function that replaces the `ContactPayload.id` value
     *   with that Contact's email address (only for this API request)
     * @param updatableContact ContactPayload
     * @returns ContactPayload
     */
    const mapIdToEmail = (updatableContact: ContactPayload): ContactPayload => {
      const contact = this._$allAccountContacts().find(({ id }) => id === updatableContact.id);
      if (!contact) {
        return updatableContact;
      }
      updatableContact.id = contact.email;
      return updatableContact;
    };
    const updatedContact = contactId ? this.$updatedContacts().find(({ id }) => id === contactId) : undefined;
    const UPDATABLE_CONTACTS = updatedContact
      ? [mapIdToEmail(updatedContact)]
      : this.$updatedContacts().map(mapIdToEmail);
    (btnElem as HTMLElement).classList.add('is-waiting');
    this._adminSvc.saveContactPermissions(UPDATABLE_CONTACTS).then(onSaveContactPermissionsSuccess);
  }

  /**
   * @description Template method handles the logic for removing a
   *   Contact from this Account
   * @param contactId ReturnType<() => SanitizedContact['id']>
   */
  protected removeContact(contactId: string): void {
    const contact = this._$allAccountContacts().find(({ id }) => id === contactId);
    if (!contact) {
      return;
    }
    const { name: CONTACT_NAME, email: CONTACT_EMAIL } = contact;
    const isConfirmed = this._lightboxSvc.openConfirmation({
      title: 'Remove User?',
      body: `Are you sure you want to remove ${CONTACT_NAME.full} from the ${this._adminSvc.accountName} account?`,
      positiveLabel: 'Remove user',
      negativeLabel: 'nevermind'
    });
    /**
     * @description
     */
    const onContactRemovedFromAccountSuccess = (): void => {
      this._$allAccountContacts.update($allAccountContacts => {
        const UPDATED_ACCOUNT_CONTACTS = $allAccountContacts.filter(({ id }) => id !== contactId);
        this._allAccountContactsCachedRoles = this._adminSvc.deepFreeze(
          this._adminSvc.getContactsRolesPayload(UPDATED_ACCOUNT_CONTACTS)
        );
        return UPDATED_ACCOUNT_CONTACTS;
      });
      this._$messenger.set({
        message: `<p>${CONTACT_NAME.full} has been removed from the ${this._adminSvc.accountName} Account.</p>`,
        type: 'notice'
      });
    };
    /**
     * @description
     */
    const onContactRemovedFromAccountError = (error: HttpErrorResponse): void => {
      void error;
      this._$messenger.set({
        message: `<p>There was a problem trying to remove ${CONTACT_NAME.full} from the ${this._adminSvc.accountName} Account. Please try again, or contact Support.</p>`,
        type: 'alert'
      });
    };
    isConfirmed.closed$.pipe(first()).subscribe(isPositive => {
      if (isPositive) {
        this._adminSvc
          .removeContactFromAccount(CONTACT_EMAIL)
          .then(onContactRemovedFromAccountSuccess)
          .catch(onContactRemovedFromAccountError);
      }
    });
  }

  /**
   * @description Template method copies Portal Roles from one given/supplied Contact
   *   to another
   * @param `change` HTML <select /> change event
   * @param thisContactId The ID of the Contact for whom the Portal Role(s) are changing
   */
  protected copyContactRoles(
    changeEvt:
      | Event
      | {
          target: {
            value: string;
          };
        },
    thisContactId: string | HTMLLIElement
  ): void {
    const peerContactId = (changeEvt.target as HTMLSelectElement).value;
    if (thisContactId instanceof HTMLLIElement) {
      const contactsListSelect = thisContactId.querySelector<HTMLSelectElement>('.contacts-list');
      if (contactsListSelect) {
        contactsListSelect.selectedIndex = 0;
      }
      return;
    }
    const peerContact = this._$allAccountContacts().find(({ id }) => id === peerContactId);
    const thisContact = this._$allAccountContacts().find(({ id }) => id === thisContactId);
    if (!peerContact || !thisContact) {
      return;
    }
    const PEER_PORTAL_ROLES = peerContact.segments;
    this._$allAccountContacts.update($allAccountContacts => {
      const updatedContact = $allAccountContacts.find(({ id }) => id === thisContactId);
      if (!updatedContact) {
        return $allAccountContacts;
      }
      updatedContact.segments.forEach((rolesGroup, rolesSegmentIndex) => {
        rolesGroup.list.forEach(thisContactPortalRole => {
          const peerRole = PEER_PORTAL_ROLES[rolesSegmentIndex]?.list.find(({ id }) => id === thisContactPortalRole.id);
          if (peerRole) {
            thisContactPortalRole.selected = peerRole.selected;
          }
        });
      });
      return structuredClone($allAccountContacts);
    });
    this._setContactUpdatability(thisContactId);
  }

  /**
   * @description Template method filters out Contact's Portal Roles (groups/segments)
   *   for the current Contact
   * @param `change` HTML <select /> change event
   * @param contactId
   * @param contactListItemElem parent `<li />` Element for the entire **Contact**
   */
  protected filterContactRoles(
    changeEvt:
      | Event
      | {
          target: {
            value: string;
          };
        },
    contactId: string,
    contactListItemElem?: HTMLLIElement
  ): void {
    const filterValue = (changeEvt.target as HTMLSelectElement).value;
    this._$allAccountContacts.update(allAccountContacts => {
      const contact = allAccountContacts.find(({ id }) => id === contactId);
      if (!contact) {
        return allAccountContacts;
      }
      contact.segments.forEach((rolesSegment: PortalRolesGroup) => {
        rolesSegment.visible = filterValue === 'all' ? true : rolesSegment.name === filterValue;
      });
      return structuredClone(allAccountContacts);
    });
    const rolesGroupSelect = contactListItemElem?.querySelector<HTMLSelectElement>('.roles-group');
    if (rolesGroupSelect) {
      rolesGroupSelect.value = 'all';
    }
  }

  /**
   * @description Template method filters out the current Contact from the list
   *   of **visible** contacts. This list populates the _Copy Roles from_ `<select>`.
   * @param contactToRemove
   * @returns `Omit<SanitizedContact, 'visible'>[]`
   */
  protected getAllVisibleContactsButOne(
    contactToRemove: Omit<SanitizedContact, 'isVisible'>
  ): Omit<SanitizedContact, 'isVisible'>[] {
    return this.$visibleAccountContacts().filter(
      (visibleAccountContact: Omit<SanitizedContact, 'isVisible'>) => visibleAccountContact !== contactToRemove
    );
  }

  /**
   * @description Template method kicks off the logic for selecting or deselecting
   *   a Portal Role for the given/provided Contact
   * @param `change` HTML (checkbox) Change Event
   * @param contactId ID of the given/provided Contact
   */
  protected modifyContactPortalRole(changeEvt: Event, contactId: string): void {
    const { target: CHECKBOX_ELEM } = changeEvt;
    const { value: PORTAL_ROLE_ID, checked: isSelected } = CHECKBOX_ELEM as HTMLInputElement;
    const queuedContact = this._$allAccountContacts().find(({ id }) => id === contactId);
    if (!queuedContact) {
      return;
    }
    const queuedRolesSegment = queuedContact.segments.find(({ list }) => list.some(({ id }) => id === PORTAL_ROLE_ID));
    const queuedRole = queuedRolesSegment?.list.find(({ id }) => id === PORTAL_ROLE_ID);
    if (!queuedRole) {
      return;
    }
    queuedRole.selected = isSelected;
    const queuedPayload = this._adminSvc.getContactsRolesPayload([queuedContact])[0];
    if (!queuedPayload) {
      return;
    }
    const isNoRolesSelected =
      queuedPayload.roles.filter(({ selected: isRoleSelected }) => isRoleSelected === true).length === 0;
    if (isNoRolesSelected) {
      this._doc.defaultView?.alert(
        `Each of ${this._adminSvc.accountName}'s available Users must have at least one Role selected.`
      );
      (CHECKBOX_ELEM as HTMLInputElement).checked = true;
    }
    this._$allAccountContacts.update($allAccountContacts => {
      const contact = $allAccountContacts.find(({ id }) => id === contactId);
      const rolesSegment = contact?.segments.find(({ list }) => list.some(({ id }) => id === PORTAL_ROLE_ID));
      const role = rolesSegment?.list.find(({ id }) => id === PORTAL_ROLE_ID);
      if (role) {
        role.selected = isNoRolesSelected ? true : isSelected;
      }
      return structuredClone($allAccountContacts);
    });
    this._setContactUpdatability(contactId);
  }

  /**
   * @description Private method determines if single Contact's permissions
   *   are different from the cached (from API) permissions.
   */
  private _setContactUpdatability(contactId: string): void {
    const cachedContact = this._allAccountContactsCachedRoles.find(({ id }) => id === contactId);
    const contact = this._$allAccountContacts().find(({ id }) => id === contactId);
    if (!cachedContact || !contact) {
      return;
    }
    const updatedContactRoles = this.$updatedContacts().find(({ id }) => id === contactId)?.roles ?? [];
    const isUpdated = updatedContactRoles.reduce((isChanged, updatedRole) => {
      if (isChanged) {
        return true;
      }
      const cachedRole = cachedContact.roles.find(({ id }) => id === updatedRole.id);
      return cachedRole ? cachedRole.selected !== updatedRole.selected : false;
    }, false);
    contact.isQueuedForUpdate = isUpdated;
    this._$allAccountContacts.set(structuredClone(this._$allAccountContacts()));
  }

  /**
   * @description Template method aliases the collection of process(es)—in
   *   the service—necessary for sending an ESAA agreement to a chosen contact
   * @param contact `Omit<SanitizedContact, 'isVisible'>`
   */
  protected sendEsaa(contact: Omit<SanitizedContact, 'isVisible'>): void {
    const onSendEsaaSuccess = (): void => {
      this._$messenger.set({
        message: `ESAA has been sent to <strong>${contact.name.full}</strong> For the ${this._adminSvc.accountName} Account.`,
        type: 'notice'
      });
      this._$allAccountContacts.update(allAccountContacts => {
        const matchedContact = allAccountContacts.find(accountContact => accountContact.id === contact.id);
        if (!matchedContact) {
          return allAccountContacts;
        }
        matchedContact.status.onboard = 'esaa sent';
        matchedContact.esaaStatus = 'pending';
        return structuredClone(allAccountContacts);
      });
    };
    const onSendEsaaError = (error: HttpErrorResponse): void => {
      void error;
      this._$messenger.set({
        message: `Could not send an ESAA at this time for ${this._adminSvc.accountName}. Please try again, or contact Support.`,
        type: 'alert'
      });
    };
    this._adminSvc.sendEsaa(contact)?.then(onSendEsaaSuccess).catch(onSendEsaaError);
  }

  protected getContactFlattenedSelectedRoles(visibleContact: Omit<SanitizedContact, 'isVisible'>): string {
    const contactPayload = this._adminSvc.getContactsRolesPayload([visibleContact])[0];
    if (!contactPayload) {
      return '';
    }
    return contactPayload.roles
      .filter(({ selected: isSelected }) => isSelected === true)
      .map(
        ({ id: selectedPortalRoleId }) =>
          this._$portalAccountRoles()
            .find(({ id }) => id === selectedPortalRoleId)
            ?.name.replaceAll(' ', '&nbsp;') ?? ''
      )
      .sort()
      .join(' | ');
  }

  /**
   * @description
   */
  protected renderNewContactInView(newContact: NewContact): void {
    this.$contactsSearchValue.set(null);
    this.$contactsSortValue.set('name--d');
    this._$allAccountContacts.update($allAccountContacts => {
      $allAccountContacts.unshift({
        name: newContact.name,
        email: newContact.email,
        id: newContact.id,
        segments: newContact.segments,
        status: {
          crmRecord: 'active',
          onboard: newContact.isExistingContact ? 'active' : 'invite sent',
          registered: false
        },
        createdOn: new Date(),
        loggedInOn: new Date('0001-01-01T00:00:00'),
        isInvitable: false,
        isQueuedForUpdate: false,
        isVisible: true,
        ...('docusignId' in newContact
          ? {
              docusignId: newContact.docusignId
            }
          : {}),
        businessSegments: newContact.businessSegments,
        esaaStatus: newContact.esaaStatus
      });
      this._allAccountContactsCachedRoles = this._adminSvc.deepFreeze(
        this._adminSvc.getContactsRolesPayload($allAccountContacts)
      );
      return structuredClone($allAccountContacts);
    });
  }

  /**
   * @description …
   */
  protected revealAddNewContactModal(): void {
    this._lightboxSvc.openComponent(AdminAddContactComponent, {
      inputs: {
        accountRoles: this.$portalAccountRoles,
        accountContacts: this.$allAccountContacts
      },
      outputs: {
        modalClosed: this._lightboxSvc.close.bind(this._lightboxSvc) as (value: unknown) => void,
        contactCreated: this.renderNewContactInView.bind(this) as (value: unknown) => void
      }
    });
  }

  /**
   * @description Template method handles logic for what to do upon an "acceptance"
   *   from the Account Admin Acknowledgment modal
   */
  protected submitAccountAdminAcknowledgment(): void {
    this._$isAccountAdminAcknowledgmentShown.set(false);
    this._lightboxSvc.close();
  }

  /**
   * @description Template method hides and reveals the Contact card/item **body**,
   *   and "resets" that card's/item's controls.
   * @param contactListItemElem HTML list-item (`<li />`) for this Contact
   * @param contactId The ID of this Contact
   */
  protected toggleContactView(contactListItemElem: HTMLLIElement, contactId: string): void {
    contactListItemElem.classList.toggle('is-expanded');
    this.filterContactRoles(
      {
        target: {
          value: 'all'
        }
      },
      contactId,
      contactListItemElem
    );
    this.copyContactRoles(
      {
        target: {
          value: ''
        }
      },
      contactListItemElem
    );
  }

  /**
   * @description Template method updates the _filter_ option used to show
   *   the list of Account Contact(s)
   * @param filterValue The **value** of the Contacts' filter control
   */
  protected updateContactsFilterOptions(filterValue: string): void {
    this.$contactsFilterOptions.update(contactsFilterOptions => {
      contactsFilterOptions.forEach(contactFilterOption => {
        contactFilterOption.selected = contactFilterOption.value === filterValue;
      });
      return structuredClone(contactsFilterOptions);
    });
  }
}
